import type { FunctionComponent } from 'react';
import { useMemo, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useAuth } from 'react-oidc-context';

import logo from '../../../../assets/logo/logo--full-horizontal.svg';
import { BalanceBreakdownComponent } from '../../../account/components/balance-breakdown/balance-breakdown';
import { useWalletTotals } from '../../../account/hooks/use-wallet-totals';
import { accountStore } from '../../../account/store/account.store';
import { Button, ButtonSize } from '../../../shared/components/button/button';
import { ContextLink } from '../../../shared/components/context-link/context-link';
import { FormattedMessage } from '../../../shared/components/formatted-message/formatted-message';
import { ImageLogo } from '../../../shared/components/image-logo/image-logo.';
import { Tab } from '../../../shared/components/tab/tab';
import { useContextNavigate } from '../../../shared/hooks/use-context-navigate';
import { useCurrentCurrency } from '../../../shared/hooks/use-current-currency';
import { useFullPath } from '../../../shared/hooks/use-full-location';
import { useSignIn } from '../../../shared/hooks/use-sign-in';
import { unique } from '../../../shared/utils/arrays';
import { createClassName, createStyleHelper } from '../../../shared/utils/class-names';
import { useAppDispatch } from '../../store/root.store';
import { FieldSectionComponent } from '../field-section/field-section';
import { UserMenu } from '../user-menu/user-menu';

import { TopNavigationView } from './large-top-navigation.interface';
import styles from './large-top-navigation.module.scss';

const style = createStyleHelper(styles, 'large-top-navigation');

export interface ILargeTopNavigationProps {
    className?: string;
    wallets?: Array<{
        type?: string | null;
        currencyCode?: string | null;
        balanceAmount?: number | null;
    }>;
    hideNavigationActions?: boolean;
}

export const LargeTopNavigation: FunctionComponent<ILargeTopNavigationProps> = ({
    className,
    wallets,
    hideNavigationActions = false,
}) => {
    const intl = useIntl();
    const [currentView, setCurrentView] = useState(TopNavigationView.CLOSED);
    const auth = useAuth();
    const signIn = useSignIn();
    const navigate = useContextNavigate();
    const returnPath = useFullPath();

    const toggleCurrentView = useCallback(
        (view: TopNavigationView) => {
            if (view === currentView) {
                setCurrentView(TopNavigationView.CLOSED);
            } else {
                setCurrentView(view);
            }
        },
        [currentView, setCurrentView],
    );

    const closeView = useCallback(() => {
        setCurrentView(TopNavigationView.CLOSED);
    }, [setCurrentView]);

    const dispatch = useAppDispatch();
    const onCurrencyChange = useCallback(
        (currency: string) => {
            dispatch(accountStore.actions.setCurrency(currency));
        },
        [dispatch],
    );

    const selectedCurrency = useCurrentCurrency();
    const allCurrencies = useMemo(
        () =>
            unique(
                wallets?.map(({ currencyCode }) => {
                    return currencyCode;
                }) ?? [],
            ).filter((value): value is string => !!value),
        [wallets],
    );
    const totalBalance = useWalletTotals(wallets);

    return (
        <div className={createClassName([className, style()])}>
            <div className={style('wrapper')}>
                <div className={style('center-outline')}>
                    <ContextLink className={style('link')} to={'/sports'}>
                        <ImageLogo className={style('logo')} srcPath={logo} />
                    </ContextLink>
                </div>
                <nav className={style('navigation')}>
                    <Tab href="/sports">
                        <FormattedMessage id="top-navigation.home" defaultMessage="HOME" />
                    </Tab>
                    <Tab href="/promo">
                        <FormattedMessage
                            id="top-navigation.crab-promo"
                            defaultMessage="PROMOTIONS"
                        />
                    </Tab>
                    <Tab href="/merchandise">
                        <FormattedMessage id="top-navigation.merchandise" defaultMessage="MERCH" />
                    </Tab>
                    {/*<Tab href="/pickem">*/}
                    {/*    <FormattedMessage id="top-navigation.splash" defaultMessage="FREE PICKEM" />*/}
                    {/*</Tab>*/}
                </nav>

                <div className={style('cta-group')}>
                    {!auth.isAuthenticated && !auth?.user ? (
                        <>
                            {!hideNavigationActions && (
                                <>
                                    <Button
                                        className={style('login')}
                                        size={ButtonSize.MEDIUM}
                                        hollow
                                        light
                                        onClick={signIn}
                                    >
                                        <FormattedMessage
                                            id="top-navigation.login"
                                            defaultMessage="LOGIN"
                                        />
                                    </Button>
                                    <Button
                                        className={style('signup')}
                                        size={ButtonSize.MEDIUM}
                                        onClick={() => navigate('/registration')}
                                    >
                                        <FormattedMessage
                                            id="top-navigation.signup"
                                            defaultMessage="SIGNUP"
                                        />
                                    </Button>
                                </>
                            )}
                        </>
                    ) : (
                        <div className={style('user-menu')}>
                            <FieldSectionComponent
                                currentView={currentView}
                                toggleCurrentView={toggleCurrentView}
                                usedView={TopNavigationView.PROFILE}
                                data-testid="name-section"
                            >
                                {auth.user?.profile && (
                                    <>
                                        {auth.user?.profile.given_name}{' '}
                                        {auth.user?.profile.family_name?.charAt(0)}
                                    </>
                                )}
                            </FieldSectionComponent>
                            <FieldSectionComponent
                                currentView={currentView}
                                toggleCurrentView={toggleCurrentView}
                                usedView={TopNavigationView.BALANCE}
                            >
                                {typeof totalBalance !== 'undefined'
                                    ? intl.formatNumber(totalBalance.total.value, {
                                          style: 'currency',
                                          currency: selectedCurrency,
                                      })
                                    : ''}
                            </FieldSectionComponent>
                            <Button
                                size={ButtonSize.MEDIUM}
                                className={style('deposit')}
                                onClick={() => navigate('/cashier/deposit')}
                            >
                                <FormattedMessage
                                    id="top-navigation.deposit"
                                    defaultMessage="Deposit"
                                />
                            </Button>
                            <Button
                                className={style('logout')}
                                hollow
                                light
                                size={ButtonSize.SMALL}
                                href={`/logout?backTo=${returnPath}`}
                            >
                                <FormattedMessage
                                    id="compact-navigation.cta.logout"
                                    defaultMessage="Logout"
                                />
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            {currentView === TopNavigationView.BALANCE ? (
                <div className={style('expanded_info')}>
                    <BalanceBreakdownComponent
                        currencies={allCurrencies}
                        currentCurrency={selectedCurrency}
                        onCurrencyChange={onCurrencyChange}
                        wallets={wallets}
                    />
                </div>
            ) : null}

            {currentView === TopNavigationView.PROFILE ? (
                <div className={style('expanded_info')}>
                    <UserMenu onClose={closeView} />
                </div>
            ) : null}
        </div>
    );
};
